import React, { useCallback, useEffect, useMemo, useState } from 'react'
import FocusLock from 'react-focus-lock'
import clsx from 'clsx'
import { MemoryRouter, Switch, useHistory } from 'react-router-dom'
import { TdsProvider } from '@instacart/tds'
import { IdsProvider } from '@instacart/ids-core'

import Portal from '../Portal/Portal'
import styles from './ActionFlow.module.scss'
import { ActionFlowContext, delay, normalizeActionFlowPath } from './ActionFlowContext'
import type {
  ActionFlowLayout,
  ActionFlowNext,
  ActionOutcome,
  ActionFlowAction,
  RunActionOptions,
} from './types'

export type ActionFlowModalProps = {
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
  initialPath: string
  overlayClassName?: string
  modalClassName?: string
  allowOverflow?: boolean
  layout?: ActionFlowLayout
}

const resolveSuccessNext = (
  outcome: ActionOutcome,
  onSuccess?: ActionFlowNext
): ActionFlowNext | undefined => {
  if (outcome.type !== 'success') return undefined
  return outcome.next ?? onSuccess
}

const resolveFailureTo = (
  outcome: ActionOutcome,
  onFailure?: { to?: string }
): string | undefined => {
  if (outcome.type !== 'failure') return undefined
  return outcome.next?.to ?? onFailure?.to
}

const ActionFlowModalInner = ({
  children,
  isOpen,
  onClose,
  allowOverflow,
  modalLayout,
}: {
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
  allowOverflow?: boolean
  modalLayout: ActionFlowLayout
}) => {
  const history = useHistory()
  const [activeLayout, setActiveLayoutState] = useState<ActionFlowLayout | undefined>(undefined)
  const [isPending, setIsPending] = useState(false)
  const [isActionInProgress, setIsActionInProgress] = useState(false)

  const close = useCallback(() => {
    onClose()
  }, [onClose])

  const setActiveLayout = useCallback((layout?: ActionFlowLayout) => {
    setActiveLayoutState(layout)
  }, [])

  const navigate = useCallback(
    (to: string) => {
      history.push(normalizeActionFlowPath(to))
    },
    [history]
  )

  const goBack = useCallback(() => {
    history.goBack()
  }, [history])

  const runAction = useCallback(
    async (
      action: ActionFlowAction,
      options?: RunActionOptions,
      e?: React.MouseEvent<HTMLButtonElement>
    ) => {
      if (isActionInProgress) {
        return {
          type: 'failure',
          error: new Error('Action already in progress'),
        } satisfies ActionOutcome
      }

      setIsActionInProgress(true)

      const origin = `${history.location.pathname}${history.location.search}${history.location.hash}`
      const pendingTo = options?.pending?.to
        ? normalizeActionFlowPath(options.pending.to)
        : undefined
      const minDelayMs = options?.pending?.minDurationMs ?? 0

      let pushedPending = false
      const delayPromise = minDelayMs > 0 ? delay(minDelayMs) : Promise.resolve()

      try {
        if (pendingTo) {
          history.push(pendingTo)
          setIsPending(true)
          pushedPending = true
        }

        const outcome = await Promise.resolve(action(e))
        await delayPromise
        setIsPending(false)

        if (outcome.type === 'success') {
          const next = resolveSuccessNext(outcome, options?.onSuccess)
          if (next?.to) {
            history.push(normalizeActionFlowPath(next.to))
          } else {
            close() // default success closes
          }
          return outcome
        }

        const failureTo = resolveFailureTo(outcome, options?.onFailure)
        if (failureTo) {
          history.push(normalizeActionFlowPath(failureTo))
        } else if (pushedPending) {
          history.goBack() // default failure navigates back to initiating page
        } else {
          const now = `${history.location.pathname}${history.location.search}${history.location.hash}`
          if (now !== origin) history.push(origin)
        }

        return outcome
      } catch (error) {
        await delayPromise
        setIsPending(false)

        const failureTo = options?.onFailure?.to
        if (failureTo) {
          history.push(normalizeActionFlowPath(failureTo))
        } else if (pushedPending) {
          history.goBack()
        } else {
          const now = `${history.location.pathname}${history.location.search}${history.location.hash}`
          if (now !== origin) history.push(origin)
        }

        return { type: 'failure' as const, error }
      } finally {
        setIsActionInProgress(false)
      }
    },
    [close, history, isActionInProgress]
  )

  const value = useMemo(
    () => ({
      isOpen,
      close,
      navigate,
      goBack,
      isPending,
      isActionInProgress,
      runAction,
      allowOverflow,
      modalLayout,
      activeLayout,
      setActiveLayout,
    }),
    [
      isOpen,
      close,
      navigate,
      goBack,
      isPending,
      isActionInProgress,
      runAction,
      allowOverflow,
      modalLayout,
      activeLayout,
      setActiveLayout,
    ]
  )

  return (
    <ActionFlowContext.Provider value={value}>
      <Switch>{children}</Switch>
    </ActionFlowContext.Provider>
  )
}

export const ActionFlowModal = ({
  children,
  isOpen,
  onClose,
  initialPath,
  overlayClassName,
  modalClassName,
  allowOverflow,
  layout = 'fullWidth',
}: ActionFlowModalProps) => {
  useEffect(() => {
    if (!isOpen) return
    document.querySelector('body')?.classList.add('no-scroll')
    return () => {
      document.querySelector('body')?.classList.remove('no-scroll')
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <TdsProvider>
      <IdsProvider>
        <Portal>
          <FocusLock autoFocus={true} data-no-focus-lock>
            <div className={clsx(styles.overlay, overlayClassName)}>
              <div
                className={clsx(
                  styles.modal,
                  allowOverflow && styles.allowOverflow,
                  modalClassName
                )}
              >
                <MemoryRouter initialEntries={[normalizeActionFlowPath(initialPath)]}>
                  <ActionFlowModalInner
                    isOpen={isOpen}
                    onClose={onClose}
                    allowOverflow={allowOverflow}
                    modalLayout={layout}
                  >
                    {children}
                  </ActionFlowModalInner>
                </MemoryRouter>
              </div>
            </div>
          </FocusLock>
        </Portal>
      </IdsProvider>
    </TdsProvider>
  )
}
